import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import Button from '../../components/ui/button';
import {
  CheckSquare,
  BarChart3,
  ShieldCheck,
  Code,
  Users,
  Globe,
  Zap
} from 'lucide-react';

const Features = () => {
  const featureCategories = [
    {
      title: 'Multi-Model Validation',
      description: 'Comprehensive testing across leading AI models',
      features: [
        'Support for GPT-4, Claude, Gemini, and more',
        'Standardized validation metrics',
        'Cross-model performance comparison'
      ],
      icon: CheckSquare,
      color: 'green'
    },
    {
      title: 'Security Assessment',
      description: 'Advanced safety and ethical AI evaluation',
      features: [
        'Bias detection algorithms',
        'Ethical response screening',
        'Vulnerability identification'
      ],
      icon: ShieldCheck,
      color: 'red'
    },
    {
      title: 'Performance Metrics',
      description: 'Detailed insights into model capabilities',
      features: [
        'Accuracy scoring',
        'Consistency tracking',
        'Contextual understanding tests'
      ],
      icon: BarChart3,
      color: 'blue'
    }
  ];

  const additionalFeatures = [
    {
      title: 'API Integration',
      description: 'Seamlessly integrate validation into your workflow',
      icon: Code
    },
    {
      title: 'Team Collaboration',
      description: 'Share insights and validation results',
      icon: Users
    },
    {
      title: 'Multi-Language Support',
      description: 'Validate models across different languages',
      icon: Globe
    },
    {
      title: 'Continuous Monitoring',
      description: 'Real-time performance tracking',
      icon: Zap
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-16">
        {/* Hero Section */}
        <section className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Comprehensive <span className="text-primary">LLM Validation</span> Features
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Powerful tools to ensure the quality, safety, and performance of your AI language models
          </p>
        </section>

        {/* Core Features */}
        <section className="mb-16">
          <div className="grid md:grid-cols-3 gap-8">
            {featureCategories.map((category, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className={`w-12 h-12 bg-${category.color}-100 rounded-lg flex items-center justify-center mb-4`}>
                    <category.icon className={`w-6 h-6 text-${category.color}-600`} />
                  </div>
                  <CardTitle>{category.title}</CardTitle>
                  <CardDescription>{category.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {category.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center">
                        <CheckSquare className="w-4 h-4 mr-2 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Additional Features */}
        <section>
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Additional Capabilities</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Extend your AI validation with advanced features designed for developers and researchers
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {additionalFeatures.map((feature, index) => (
              <Card key={index} className="text-center border-0 shadow-md hover:shadow-lg transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center mt-16">
          <h2 className="text-3xl font-bold mb-6">Ready to Validate Your Models?</h2>
          <Link to="/waitlist">
            <Button size="lg" className="mx-auto">
              Start Free Validation
            </Button>
          </Link>
        </section>
      </div>
    </div>
  );
};

export default Features;
